/**
 * Files (ADR-0049): the person's cloud storage — Seafile, Nextcloud, ownCloud —
 * beside their mail. Each connected account is a root on the left; a person
 * may connect several, of one kind or many. Connecting is always the
 * provider's own sign-in page, so whatever single sign-on the provider uses
 * is what the person sees; ZitiMail never asks for a storage password.
 *
 * The same browser picks a file to attach, or a folder to save into, from
 * the composer and the reader (`FilePicker`).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  StorageAccount,
  StorageEntry,
  StorageOverview,
  StorageServer,
} from 'zitimail-shared-types';

import { ApiError } from '../admin/api';
import { KIND_NAMES, storage, uploadToStorage } from '../api/storage';
import {
  CheckIcon,
  ChevronRightIcon,
  CloseIcon,
  DocumentIcon,
  DownloadIcon,
  FilesIcon,
  LinkIcon,
  PlusIcon,
  RefreshIcon,
  TrashIcon,
  UploadIcon,
} from '../components/icons';
import { useToast } from '../components/Toasts';
import { size } from '../mail/format';
import './files.css';

const message = (e: unknown, fallback: string) =>
  e instanceof Error && e.message ? e.message : fallback;

/** A provider's mark: its initial, in the provider's own colour. */
function KindMark({ kind }: { kind: StorageAccount['kind'] }): JSX.Element {
  return (
    <span className={`kind-mark kind-${kind}`} aria-hidden="true">
      {KIND_NAMES[kind].charAt(0)}
    </span>
  );
}

function when(iso: string | null): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Where the browser is: the account's root, then each folder opened. */
export interface Crumb {
  readonly name: string;
  readonly path: string;
}

export type PickMode =
  | { readonly kind: 'browse' }
  | { readonly kind: 'file'; readonly onPick: (entry: StorageEntry) => void }
  | { readonly kind: 'folder'; readonly onPick: (folder: Crumb) => void; readonly action: string };

/** A folder of one account: open folders, and act on files. */
export function Browser({
  slug,
  account,
  mode,
}: {
  slug: string;
  account: StorageAccount;
  mode: PickMode;
}): JSX.Element {
  const toast = useToast();
  const [trail, setTrail] = useState<Crumb[]>([{ name: account.label, path: '/' }]);
  const [entries, setEntries] = useState<readonly StorageEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<Array<{ id: number; name: string; progress: number }>>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const next = useRef(0);
  const here = trail[trail.length - 1] ?? { name: account.label, path: '/' };
  // Seafile and ownCloud list libraries and spaces at the top, which are
  // not folders a file can be put into.
  const topOfSpaces = here.path === '/' && account.kind !== 'nextcloud';

  useEffect(() => {
    setTrail([{ name: account.label, path: '/' }]);
  }, [account.id, account.label]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setEntries(null);
      setError(null);
      storage
        .items(slug, account.id, here.path, signal)
        .then((listing) => setEntries(listing.entries))
        .catch((e: unknown) => {
          if (signal?.aborted) {
            return;
          }
          setError(
            e instanceof ApiError && e.code === 'reconnect'
              ? `${account.server_name} no longer accepts this connection. Disconnect it and connect again.`
              : message(e, 'This folder could not be opened.'),
          );
        });
    },
    [slug, account.id, account.server_name, here.path],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const upload = (files: FileList | null) => {
    if (!files) {
      return;
    }
    for (const file of [...files]) {
      next.current += 1;
      const id = next.current;
      setUploads((u) => [...u, { id, name: file.name, progress: 0 }]);
      uploadToStorage(account.id, here.path, file, file.name, (progress) =>
        setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress } : x))),
      )
        .done.then((saved) => {
          toast(
            saved.scanned
              ? `${saved.name} saved to ${account.label}.`
              : `${saved.name} saved to ${account.label}, not checked for malware (too large).`,
          );
          load();
        })
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === 'AbortError') {
            return;
          }
          toast(`${file.name}: ${message(e, 'not saved')}`, { tone: 'error' });
        })
        .finally(() => setUploads((u) => u.filter((x) => x.id !== id)));
    }
  };

  const share = async (entry: StorageEntry) => {
    setBusy(entry.path);
    try {
      const link = await storage.link(slug, account.id, entry.path);
      const text = link.password ? `${link.url}\nPassword: ${link.password}` : link.url;
      await navigator.clipboard?.writeText(text).catch(() => undefined);
      toast(
        link.password
          ? `Link to ${entry.name} copied, with its password (${account.server_name} requires one).`
          : `Link to ${entry.name} copied. It works until ${when(link.expires_at)}.`,
      );
    } catch (e) {
      toast(message(e, 'No link was made.'), { tone: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="file-browser">
      <div className="file-bar">
        <nav className="crumbs" aria-label="Folder">
          {trail.map((crumb, i) => (
            <span key={crumb.path} className="crumb">
              {i > 0 && <ChevronRightIcon />}
              {i === trail.length - 1 ? (
                <span aria-current="location">{crumb.name}</span>
              ) : (
                <button
                  type="button"
                  className="text-btn"
                  onClick={() => setTrail(trail.slice(0, i + 1))}
                >
                  {crumb.name}
                </button>
              )}
            </span>
          ))}
        </nav>
        <span className="file-bar-acts">
          <button
            type="button"
            className="icon-btn"
            title="Refresh"
            aria-label="Refresh"
            onClick={() => load()}
          >
            <RefreshIcon />
          </button>
          {mode.kind === 'browse' && !topOfSpaces && (
            <>
              <button type="button" className="text-btn" onClick={() => fileInput.current?.click()}>
                <UploadIcon /> Upload
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  upload(e.target.files);
                  e.target.value = '';
                }}
              />
            </>
          )}
          {mode.kind === 'folder' && !topOfSpaces && (
            <button type="button" className="btn compact" onClick={() => mode.onPick(here)}>
              {mode.action}
            </button>
          )}
        </span>
      </div>

      {uploads.map((u) => (
        <div key={u.id} className="file-progress" role="status">
          <span>{u.name}</span>
          <progress value={u.progress} max={1} aria-label={`Uploading ${u.name}`} />
        </div>
      ))}

      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      {!error && entries === null && <p className="file-empty">Opening…</p>}
      {entries && entries.length === 0 && (
        <p className="file-empty">
          {topOfSpaces ? 'Nothing here yet.' : 'This folder is empty.'}
          {mode.kind === 'browse' && !topOfSpaces && ' Upload a file to put it here.'}
        </p>
      )}
      {entries && entries.length > 0 && (
        <table className="file-table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col" className="num">
                Size
              </th>
              <th scope="col" className="file-when">
                Modified
              </th>
              {mode.kind === 'browse' && <th scope="col" aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.path}>
                <td>
                  {entry.folder ? (
                    <button
                      type="button"
                      className="file-name"
                      onClick={() => setTrail([...trail, { name: entry.name, path: entry.path }])}
                    >
                      <FilesIcon /> {entry.name}
                    </button>
                  ) : mode.kind === 'file' ? (
                    <button type="button" className="file-name" onClick={() => mode.onPick(entry)}>
                      <DocumentIcon /> {entry.name}
                    </button>
                  ) : (
                    <span className="file-name">
                      <DocumentIcon /> {entry.name}
                    </span>
                  )}
                </td>
                <td className="num">{entry.size === null ? '' : size(entry.size)}</td>
                <td className="file-when">{when(entry.modified)}</td>
                {mode.kind === 'browse' && (
                  <td className="file-acts">
                    {!entry.folder && (
                      <>
                        <a
                          className="icon-btn"
                          href={storage.downloadUrl(slug, account.id, entry.path)}
                          title={`Download ${entry.name}`}
                          aria-label={`Download ${entry.name}`}
                        >
                          <DownloadIcon />
                        </a>
                        <button
                          type="button"
                          className="icon-btn"
                          title={`Copy a link to ${entry.name}`}
                          aria-label={`Copy a link to ${entry.name}`}
                          disabled={busy === entry.path}
                          onClick={() => void share(entry)}
                        >
                          <LinkIcon />
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Connecting: choose a server, then sign in on its own page (a new window;
 * the desktop app opens it in the system browser). This page watches for
 * the sign-in to finish.
 */
function Connect({
  slug,
  servers,
  onConnected,
  onCancel,
}: {
  slug: string;
  servers: readonly StorageServer[];
  onConnected: (account: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [server, setServer] = useState<StorageServer | null>(null);
  const [label, setLabel] = useState('');
  const [started, setStarted] = useState<{ attempt: string; url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!started) {
      return;
    }
    let stopped = false;
    const deadline = Date.now() + 10 * 60_000;
    const check = async () => {
      if (stopped) {
        return;
      }
      try {
        const status = await storage.status(slug, started.attempt);
        if (status.status === 'connected' && status.account) {
          onConnected(status.account);
          return;
        }
        if (status.status === 'failed') {
          setError(status.error ?? 'The sign-in did not finish.');
          setStarted(null);
          return;
        }
      } catch (e) {
        setError(message(e, 'The sign-in could not be checked.'));
        setStarted(null);
        return;
      }
      if (Date.now() > deadline) {
        setError('The sign-in took too long. Start again.');
        setStarted(null);
        return;
      }
      window.setTimeout(() => void check(), 2000);
    };
    const first = window.setTimeout(() => void check(), 2000);
    return () => {
      stopped = true;
      window.clearTimeout(first);
    };
  }, [started, slug, onConnected]);

  const begin = async () => {
    if (!server) {
      return;
    }
    setWorking(true);
    setError(null);
    try {
      const start = await storage.connect(slug, server.id, label.trim() || undefined);
      setStarted({ attempt: start.attempt, url: start.url });
    } catch (e) {
      setError(message(e, 'The sign-in could not start.'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="connect-pane" aria-label="Connect storage">
      <div className="connect-head">
        <h2>Connect storage</h2>
        <button type="button" className="icon-btn" aria-label="Cancel" onClick={onCancel}>
          <CloseIcon />
        </button>
      </div>
      {servers.length === 0 ? (
        <p className="file-empty">
          Your organisation has not added a storage server yet. Your administrator adds them in
          Admin → Files.
        </p>
      ) : started ? (
        <div className="connect-wait">
          <p>
            Sign in to <strong>{server?.name}</strong> on its own page. Use your usual sign-in;
            ZitiMail never sees your storage password.
          </p>
          <a className="btn" href={started.url} target="_blank" rel="noopener noreferrer">
            Sign in to {server?.name}
          </a>
          <p className="connect-note" role="status">
            Waiting for the sign-in to finish… This page updates by itself.
          </p>
          <button type="button" className="text-btn" onClick={() => setStarted(null)}>
            Start again
          </button>
        </div>
      ) : (
        <>
          <fieldset className="server-choice">
            <legend>Where are your files?</legend>
            {servers.map((s) => (
              <label key={s.id} className={`server-option${server?.id === s.id ? ' on' : ''}`}>
                <input
                  type="radio"
                  name="server"
                  checked={server?.id === s.id}
                  onChange={() => setServer(s)}
                />
                <KindMark kind={s.kind} />
                <span className="server-text">
                  <span className="server-name">
                    {s.name}
                    {s.genworx && <span className="badge">Genworx</span>}
                  </span>
                  <span className="server-sub">
                    {KIND_NAMES[s.kind]} · {s.base_url.replace(/^https?:\/\//, '')}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          <label className="connect-label">
            Name it (optional)
            <input
              value={label}
              maxLength={80}
              placeholder={server?.name ?? 'e.g. Work files'}
              onChange={(e) => setLabel(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn"
            disabled={!server || working}
            onClick={() => void begin()}
          >
            Continue
          </button>
        </>
      )}
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
    </section>
  );
}

/** One connection's own settings: its name, default, disconnect. */
function AccountBar({
  slug,
  account,
  onChanged,
}: {
  slug: string;
  account: StorageAccount;
  onChanged: () => void;
}): JSX.Element {
  const toast = useToast();
  const [renaming, setRenaming] = useState<string | null>(null);
  const act = async (run: () => Promise<void>, done: string) => {
    try {
      await run();
      toast(done);
      onChanged();
    } catch (e) {
      toast(message(e, 'Not changed.'), { tone: 'error' });
    }
  };
  return (
    <div className="account-bar">
      {renaming === null ? (
        <span className="account-title">
          <KindMark kind={account.kind} />
          <span>
            <strong>{account.label}</strong>
            <span className="server-sub">
              {account.remote_name} · {account.server_name}
            </span>
          </span>
        </span>
      ) : (
        <form
          className="account-rename"
          onSubmit={(e) => {
            e.preventDefault();
            const label = renaming.trim();
            setRenaming(null);
            if (label && label !== account.label) {
              void act(() => storage.rename(slug, account.id, label), `Renamed to ${label}.`);
            }
          }}
        >
          <input
            value={renaming}
            maxLength={80}
            autoFocus
            aria-label="Name"
            onChange={(e) => setRenaming(e.target.value)}
          />
          <button type="submit" className="icon-btn" aria-label="Save name">
            <CheckIcon />
          </button>
        </form>
      )}
      <span className="account-acts">
        <button type="button" className="text-btn" onClick={() => setRenaming(account.label)}>
          Rename
        </button>
        {!account.is_default && (
          <button
            type="button"
            className="text-btn"
            onClick={() =>
              void act(
                () => storage.makeDefault(slug, account.id),
                `${account.label} opens first when you save to Files.`,
              )
            }
          >
            Make default
          </button>
        )}
        <button
          type="button"
          className="text-btn danger"
          onClick={() => {
            if (
              window.confirm(
                `Disconnect ${account.label}? Your files stay in ${account.server_name}; ZitiMail gives back its access.`,
              )
            ) {
              void act(
                () => storage.disconnect(slug, account.id),
                `${account.label} disconnected.`,
              );
            }
          }}
        >
          <TrashIcon /> Disconnect
        </button>
      </span>
    </div>
  );
}

/** The accounts on the left; shared by Files and the picker. */
function AccountList({
  accounts,
  selected,
  onSelect,
  onConnect,
}: {
  accounts: readonly StorageAccount[];
  selected: string | null;
  onSelect: (id: string) => void;
  onConnect?: () => void;
}): JSX.Element {
  return (
    <ul className="storage-accounts">
      {accounts.map((a) => (
        <li key={a.id}>
          <button
            type="button"
            className={`storage-account${selected === a.id ? ' on' : ''}`}
            onClick={() => onSelect(a.id)}
          >
            <KindMark kind={a.kind} />
            <span className="server-text">
              <span className="server-name">
                {a.label}
                {a.is_default && <span className="badge">Default</span>}
              </span>
              <span className="server-sub">{a.remote_name}</span>
            </span>
          </button>
        </li>
      ))}
      {onConnect && (
        <li>
          <button type="button" className="storage-account connect" onClick={onConnect}>
            <span className="kind-mark plus" aria-hidden="true">
              <PlusIcon />
            </span>
            <span className="server-name">Connect storage</span>
          </button>
        </li>
      )}
    </ul>
  );
}

function useOverview(slug: string) {
  const [overview, setOverview] = useState<StorageOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => {
    storage
      .overview(slug)
      .then((o) => {
        setOverview(o);
        setError(null);
      })
      .catch((e: unknown) => setError(message(e, 'Files could not be opened.')));
  }, [slug]);
  useEffect(reload, [reload]);
  return { overview, error, reload };
}

/** The Files view, from the rail. */
export function FilesView({ slug }: { slug: string }): JSX.Element {
  const toast = useToast();
  const { overview, error, reload } = useOverview(slug);
  const [selected, setSelected] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  const accounts = overview?.accounts ?? [];
  const account = accounts.find((a) => a.id === selected) ?? accounts[0] ?? null;

  const connected = useCallback(
    (id: string) => {
      setConnecting(false);
      setSelected(id);
      toast('Connected.');
      reload();
    },
    [reload, toast],
  );

  if (error) {
    return (
      <main className="main files-layout">
        <div className="state">
          <div className="alert" role="alert">
            {error}
          </div>
        </div>
      </main>
    );
  }
  if (!overview) {
    return <main className="main files-layout" aria-busy="true" />;
  }
  if (!overview.enabled) {
    return (
      <main className="main files-layout">
        <div className="files-off">
          <FilesIcon />
          <h2>Files is not on for your organisation</h2>
          <p>
            Files brings your Seafile, Nextcloud or ownCloud storage next to your mail. Your
            administrator turns it on in Admin → Files.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="main files-layout">
      <section className="files-side" aria-label="Your storage">
        <h3 className="files-side-head">Storage</h3>
        <AccountList
          accounts={accounts}
          selected={connecting ? null : (account?.id ?? null)}
          onSelect={(id) => {
            setConnecting(false);
            setSelected(id);
          }}
          onConnect={() => setConnecting(true)}
        />
        <p className="files-note">
          Every file you move between mail and storage is checked for malware.
        </p>
      </section>
      <section className="files-pane" aria-label="Files">
        {connecting || !account ? (
          <Connect
            slug={slug}
            servers={overview.servers}
            onConnected={connected}
            onCancel={() => setConnecting(false)}
          />
        ) : (
          <>
            <AccountBar slug={slug} account={account} onChanged={reload} />
            <Browser key={account.id} slug={slug} account={account} mode={{ kind: 'browse' }} />
          </>
        )}
      </section>
    </main>
  );
}

export type PickerMode =
  | {
      readonly kind: 'file';
      readonly onPick: (account: StorageAccount, entry: StorageEntry) => void;
    }
  | {
      readonly kind: 'folder';
      readonly action: string;
      readonly onPick: (account: StorageAccount, folder: Crumb) => void;
    };

/**
 * A dialog over the composer or the reader: pick a file to attach, or the
 * folder to save into. Opens on the default account.
 */
export function FilePicker({
  slug,
  title,
  mode,
  onClose,
}: {
  slug: string;
  title: string;
  mode: PickerMode;
  onClose: () => void;
}): JSX.Element {
  const { overview, error } = useOverview(slug);
  const [selected, setSelected] = useState<string | null>(null);
  const accounts = overview?.accounts ?? [];
  const account =
    accounts.find((a) => a.id === selected) ?? accounts.find((a) => a.is_default) ?? accounts[0];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        className="file-picker"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="connect-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        {overview && (!overview.enabled || accounts.length === 0) && (
          <p className="file-empty">
            {overview.enabled
              ? 'Connect your storage in Files first (the folder in the left rail).'
              : 'Files is not on for your organisation.'}
          </p>
        )}
        {overview && account && (
          <div className="picker-body">
            {accounts.length > 1 && (
              <AccountList accounts={accounts} selected={account.id} onSelect={setSelected} />
            )}
            <Browser
              key={account.id}
              slug={slug}
              account={account}
              mode={
                mode.kind === 'file'
                  ? { kind: 'file', onPick: (entry) => mode.onPick(account, entry) }
                  : {
                      kind: 'folder',
                      action: mode.action,
                      onPick: (folder) => mode.onPick(account, folder),
                    }
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
